import { useTranslation } from "react-i18next";
import { useCurrentLang, getAltLangUrl } from "@/hooks/useLanguage";
import { useLocation } from "react-router-dom";
import { useMemo } from "react";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();
  const lang = useCurrentLang();
  const location = useLocation();
  const isFrench = lang === "fr";
  const prefix = isFrench ? "/fr" : "";
  const altLangUrl = useMemo(() => getAltLangUrl(location.pathname), [location.pathname]);

  return (
    <footer className="w-full bg-[#0d1f4c]">
      {/* Main footer */}
      <div className="max-w-[1200px] mx-auto px-6 py-7">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Column 1 – Brand */}
          <div className="lg:col-span-1">
            <a href={prefix || "/"} className="inline-block mb-4">
              <img
                src="/wp-content/uploads/2016/04/Mpf_logo-April2016.png"
                alt="Musicians' Pension Fund of Canada"
                className="h-16 w-auto object-contain brightness-0 invert"
              />
            </a>
            <p className="text-sm text-white/60 leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>

          {/* Column 2 – THE PLAN */}
          <div>
            <h4 className="text-xs font-semibold text-white/40 tracking-widest uppercase mb-3">
              {t("footer.thePlan")}
            </h4>
            <ul className="space-y-2">
              <li>
                <a href={`${prefix || "/"}#about`} className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                  {t("footer.aboutTheFund")}
                </a>
              </li>
              <li>
                <a href={`${prefix || "/"}#summary-plan`} className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                  {t("footer.summaryPlan")}
                </a>
              </li>
              <li>
                <a href={`${prefix || "/"}#plan-rules`} className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                  {t("footer.planRules")}
                </a>
              </li>
              <li>
                <a href={`${prefix}/faq`} className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                  {t("footer.faq")}
                </a>
              </li>
              <li>
                <a href={`${prefix}/news`} className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                  {t("footer.news")}
                </a>
              </li>
              <li>
                <a href={`${prefix}/contact`} className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                  {t("footer.contactUs")}
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3 – RESOURCES */}
          <div>
            <h4 className="text-xs font-semibold text-white/40 tracking-widest uppercase mb-3">
              {t("footer.resources")}
            </h4>
            <ul className="space-y-2">
              <li>
                <a href={`${prefix}/resources?category=annual-reports`} className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                  {t("footer.annualReports")}
                </a>
              </li>
              <li>
                <a href={`${prefix}/resources?category=newsletters`} className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                  {t("footer.newsletters")}
                </a>
              </li>
              <li>
                <a href={`${prefix}/resources?category=forms`} className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                  {t("footer.forms")}
                </a>
              </li>
              <li>
                <a href={`${prefix}/resources?category=what-happens-if`} className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                  {t("footer.whatHappensIf")}
                </a>
              </li>
              <li>
                <a href={`${prefix}/resources?category=key-plan-definitions`} className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                  {t("footer.keyPlanDefinitions")}
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4 – CONTACT */}
          <div>
            <h4 className="text-xs font-semibold text-white/40 tracking-widest uppercase mb-3">
              {t("footer.contact")}
            </h4>
            <ul className="space-y-2.5">
              <li className="flex items-start gap-2.5">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-map-pin-line text-[#1a8fc1] text-sm"></i>
                </div>
                <span className="text-sm text-white/60 leading-relaxed whitespace-pre-line">
                  {t("footer.address")}
                </span>
              </li>
              <li className="flex items-center gap-2.5">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-[#1a8fc1] text-sm"></i>
                </div>
                <span className="text-sm text-white/60">
                  416-497-4702
                </span>
              </li>
              <li className="flex items-center gap-2.5">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-[#1a8fc1] text-sm"></i>
                </div>
                <span className="text-sm text-white/60">
                  {t("footer.tollFree")}
                </span>
              </li>
              <li className="flex items-center gap-2.5">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-printer-line text-[#1a8fc1] text-sm"></i>
                </div>
                <span className="text-sm text-white/60">
                  {t("footer.fax")}
                </span>
              </li>
              <li className="flex items-center gap-2.5">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-[#1a8fc1] text-sm"></i>
                </div>
                <a href="mailto:info@mpfcanada.ca" className="text-sm text-[#7ec8e3] hover:text-white hover:underline transition-colors cursor-pointer">
                  info@mpfcanada.ca
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/40">
            {t("footer.copyright", { year: currentYear })}
          </p>
          <div className="flex items-center gap-4">
            <a
              href={isFrench ? altLangUrl : (location.pathname + location.search)}
              className={`text-xs hover:text-white/70 transition-colors cursor-pointer ${!isFrench ? "text-white/70 font-semibold" : "text-white/40"}`}
            >
              {t("header.english")}
            </a>
            <span className="text-white/20 text-xs">|</span>
            <a
              href={isFrench ? (location.pathname + location.search) : altLangUrl}
              className={`text-xs hover:text-white/70 transition-colors cursor-pointer ${isFrench ? "text-white/70 font-semibold" : "text-white/40"}`}
            >
              {t("header.francais")}
            </a>
            <span className="text-white/20 text-xs">|</span>
            <a
              href={isFrench ? "https://www.mpfcanada.ca/wp-content/2026/privacy-fr.html" : "https://www.mpfcanada.ca/wp-content/2026/privacy.html"}
              target="_blank"
              rel="nofollow"
              className="text-xs text-white/40 hover:text-white/70 transition-colors cursor-pointer"
            >
              {t("footer.privacyPolicy")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}